/**
 * Tabblad Prompts: de teksten waarmee de personages, Socrates en de hulp
 * worden aangestuurd. Een systeemprompt is terug te zetten naar de
 * standaardtekst uit prompt_seeder.py; een eigen prompt is te verwijderen.
 */
import React, { useEffect, useState } from 'react';
import { Plus, Save, Trash2, RefreshCw } from 'lucide-react';
import { kleur } from '../../../theme/tokens';
import { basis } from '../stijlen';

const LEGE_PROMPT = { category: '', key: '', name: '', description: '', prompt_text: '', sort_order: 0 };

export function Prompts({ api }) {
  const [prompts, setPrompts] = useState([]);
  const [categorieen, setCategorieen] = useState([]);
  const [categorie, setCategorie] = useState('');
  const [laden, setLaden] = useState(false);
  const [gekozen, setGekozen] = useState(null);
  const [bewerkt, setBewerkt] = useState(null);
  const [nieuw, setNieuw] = useState(null);

  const haalCategorieen = async () => {
    try {
      const data = await api.haalPromptCategorieen();
      if (data.success) setCategorieen(data.data);
    } catch (err) { console.error('Fout bij laden categorieën:', err); }
  };

  const haalPrompts = async (cat = null) => {
    setLaden(true);
    try {
      const data = await api.haalPrompts(cat);
      if (data.success) setPrompts(data.data);
    } catch (err) { console.error('Fout bij laden prompts:', err); }
    finally { setLaden(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { haalCategorieen(); haalPrompts(); }, []);

  const herlaad = () => { haalPrompts(categorie); haalCategorieen(); };

  const slaOp = async () => {
    try {
      const data = await api.wijzigPrompt(bewerkt);
      if (data.success) {
        setBewerkt(null);
        haalPrompts(categorie);
        if (gekozen?.id === bewerkt.id) setGekozen(data.data);
      }
    } catch (err) { console.error('Fout bij opslaan prompt:', err); }
  };

  const maak = async (e) => {
    e.preventDefault();
    try {
      const data = await api.maakPrompt(nieuw);
      if (data.success) { setNieuw(null); herlaad(); }
      else alert(data.detail || 'Fout bij aanmaken prompt');
    } catch (err) { console.error('Fout bij aanmaken prompt:', err); }
  };

  const verwijder = async (id) => {
    if (!window.confirm('Weet je zeker dat je deze prompt wilt verwijderen?')) return;
    try {
      const data = await api.verwijderPrompt(id);
      if (data.success) { setGekozen(null); herlaad(); }
      else alert(data.detail || 'Fout bij verwijderen');
    } catch (err) { console.error('Fout:', err); }
  };

  const zetTerug = async (id) => {
    if (!window.confirm('Weet je zeker dat je deze prompt wilt resetten naar de standaard waarde?')) return;
    try {
      const data = await api.zetPromptTerug(id);
      if (data.success) { setGekozen(data.data); haalPrompts(categorie); }
      else alert(data.detail || 'Fout bij resetten');
    } catch (err) { console.error('Fout:', err); }
  };

  const laadStandaard = async () => {
    try {
      const data = await api.laadStandaardPrompts();
      if (data.success) { alert(data.message); herlaad(); }
    } catch (err) { console.error('Fout:', err); }
  };

  return (
    <div style={styles.splitView}>
      <div style={styles.listPanel}>
        <div style={styles.panelHeader}>
          <h2>Prompts</h2>
          <button onClick={() => setNieuw(LEGE_PROMPT)} style={styles.addBtn}><Plus size={16} /> Nieuwe Prompt</button>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <select value={categorie} style={styles.input}
            onChange={(e) => { setCategorie(e.target.value); haalPrompts(e.target.value || null); setGekozen(null); }}>
            <option value="">Alle categorieën</option>
            {categorieen.map(cat => <option key={cat.key} value={cat.key}>{cat.name} ({cat.prompt_count})</option>)}
          </select>
        </div>

        {prompts.length === 0 && (
          <div style={{ marginBottom: '16px' }}>
            <button onClick={laadStandaard} style={styles.saveBtn}><RefreshCw size={14} /> Standaard prompts laden</button>
          </div>
        )}

        {nieuw && (
          <form onSubmit={maak} style={styles.form}>
            <select value={nieuw.category} onChange={(e) => setNieuw({ ...nieuw, category: e.target.value })} style={styles.input} required>
              <option value="">-- Kies categorie --</option>
              <option value="persona_chat">Persona Chat</option>
              <option value="discussieleider">Discussieleider</option>
              <option value="feedback">Feedback</option>
              <option value="systeem">Systeem</option>
            </select>
            <input type="text" placeholder="Key (unieke identifier)" value={nieuw.key} onChange={(e) => setNieuw({ ...nieuw, key: e.target.value })} style={styles.input} required />
            <input type="text" placeholder="Naam" value={nieuw.name} onChange={(e) => setNieuw({ ...nieuw, name: e.target.value })} style={styles.input} required />
            <input type="text" placeholder="Beschrijving (optioneel)" value={nieuw.description} onChange={(e) => setNieuw({ ...nieuw, description: e.target.value })} style={styles.input} />
            <textarea placeholder="Prompt tekst" value={nieuw.prompt_text} onChange={(e) => setNieuw({ ...nieuw, prompt_text: e.target.value })} style={{ ...styles.input, minHeight: '120px', fontFamily: 'monospace' }} required />
            <input type="number" placeholder="Sorteervolgorde" value={nieuw.sort_order} onChange={(e) => setNieuw({ ...nieuw, sort_order: parseInt(e.target.value) || 0 })} style={styles.input} />
            <div style={styles.formButtons}>
              <button type="submit" style={styles.saveBtn}><Save size={16} /> Opslaan</button>
              <button type="button" onClick={() => setNieuw(null)} style={styles.cancelBtn}>Annuleren</button>
            </div>
          </form>
        )}

        {laden ? <p>Laden...</p> : prompts.length === 0 ? <p style={styles.empty}>Geen prompts gevonden.</p> : (
          <ul style={styles.list}>
            {prompts.map((prompt) => (
              <li key={prompt.id} style={gekozen?.id === prompt.id ? styles.listItemActive : styles.listItem} onClick={() => setGekozen(prompt)}>
                <div>
                  <span>{prompt.name}</span>
                  <div style={{ fontSize: '12px', color: kleur.stil }}>{prompt.key}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {prompt.is_system && <span style={styles.badge}>systeem</span>}
                  {!prompt.is_active && <span style={{ ...styles.badge, background: kleur.rood }}>inactief</span>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div style={styles.detailPanel}>
        {!gekozen ? <p style={styles.empty}>Selecteer een prompt</p> : bewerkt ? (
          <div style={styles.editForm}>
            <h2>Bewerken: {gekozen.name}</h2>
            <div style={{ height: '12px' }}></div>
            <label style={styles.label}>Naam</label>
            <input type="text" value={bewerkt.name || ''} onChange={(e) => setBewerkt({ ...bewerkt, name: e.target.value })} style={styles.input} />
            <label style={styles.label}>Beschrijving</label>
            <input type="text" value={bewerkt.description || ''} onChange={(e) => setBewerkt({ ...bewerkt, description: e.target.value })} style={styles.input} />
            <label style={styles.label}>Prompt tekst</label>
            <textarea value={bewerkt.prompt_text || ''} onChange={(e) => setBewerkt({ ...bewerkt, prompt_text: e.target.value })}
              style={{ ...styles.input, minHeight: '300px', fontFamily: 'monospace', fontSize: '13px' }} />
            <div style={styles.row}>
              <div style={{ flex: 1 }}>
                <label style={styles.label}>Sorteervolgorde</label>
                <input type="number" value={bewerkt.sort_order || 0} onChange={(e) => setBewerkt({ ...bewerkt, sort_order: parseInt(e.target.value) || 0 })} style={styles.input} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={styles.label}>Status</label>
                <label style={{ ...styles.checkboxLabel, marginTop: '8px' }}>
                  <input type="checkbox" checked={bewerkt.is_active} onChange={(e) => setBewerkt({ ...bewerkt, is_active: e.target.checked })} />
                  Actief
                </label>
              </div>
            </div>
            <div style={{ height: '16px' }}></div>
            <div style={styles.formButtons}>
              <button type="button" onClick={slaOp} style={styles.saveBtn}><Save size={16} /> Opslaan</button>
              <button type="button" onClick={() => setBewerkt(null)} style={styles.cancelBtn}>Annuleren</button>
            </div>
          </div>
        ) : (
          <>
            <div style={styles.panelHeader}>
              <h2>{gekozen.name}</h2>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => setBewerkt({ ...gekozen })} style={styles.addBtn}>Bewerken</button>
                {gekozen.is_system ? (
                  <button onClick={() => zetTerug(gekozen.id)} style={{ ...styles.addBtn, background: kleur.oranje }}><RefreshCw size={14} /> Reset</button>
                ) : (
                  <button onClick={() => verwijder(gekozen.id)} style={styles.deleteBtn}><Trash2 size={16} /> Verwijderen</button>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
              <span style={styles.badge}>{gekozen.category}</span>
              {gekozen.is_system && <span style={{ ...styles.badge, background: kleur.paars }}>systeem</span>}
              {gekozen.is_active
                ? <span style={{ ...styles.badge, background: kleur.groen }}>actief</span>
                : <span style={{ ...styles.badge, background: kleur.rood }}>inactief</span>}
            </div>
            <p style={styles.beschrijving}><strong>Key:</strong> {gekozen.key}</p>
            {gekozen.description && <p style={styles.beschrijving}>{gekozen.description}</p>}
            <h3 style={styles.subheader}>Prompt tekst</h3>
            <div style={{ height: '8px' }}></div>
            <pre style={styles.promptPreview}>{gekozen.prompt_text}</pre>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  ...basis,
  promptPreview: { background: kleur.vlak, padding: '16px', borderRadius: '8px', fontFamily: 'monospace', fontSize: '13px', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '400px', overflowY: 'auto', border: `1px solid ${kleur.lijn}` },
};
